import { CommonModule } from "@angular/common";

import { Component, EventEmitter, Input, Output } from "@angular/core";

import { FormsModule } from "@angular/forms";

import { finalize } from "rxjs/operators";

import {
  GlogetSalonSetup,
  GlogetStaffSetup,
  IntegrationsService,
  SquareSetupResponse,
  SquareStaffSetup,
} from "../../../../../../shared/services/integrations.service";

interface CreateGlogetStaffForm {
  square_team_member_id: string;

  salon_id: string;

  first_name: string;

  last_name: string;

  email: string;

  phone_number: string;

  job_title: string;
}

@Component({
  selector: "app-square-staff",

  standalone: true,

  imports: [CommonModule, FormsModule],

  templateUrl: "./square-staff.component.html",

  styleUrl: "./square-staff.component.scss",
})
export class SquareStaffComponent {
  @Input({
    required: true,
  })
  setup!: SquareSetupResponse;

  @Output()
  mappingChanged = new EventEmitter<void>();

  selectedStaffBySquareMember: Record<string, string> = {};

  mappingTeamMemberId: string | null = null;

  creatingSquareStaffId: string | null = null;

  successMessage = "";

  errorMessage = "";

  /* ========================================================
     Create gloget staff modal
  ======================================================== */

  showCreateGlogetModal = false;

  selectedSquareMember: SquareStaffSetup | null = null;

  createGlogetSubmitting = false;

  createGlogetError = "";

  createGlogetForm: CreateGlogetStaffForm = this.emptyForm();

  constructor(private readonly integrationsService: IntegrationsService) {}

  get squareStaff(): SquareStaffSetup[] {
    return this.setup?.staff.square ?? [];
  }

  get glogetStaff(): GlogetStaffSetup[] {
    return this.setup?.staff.gloget ?? [];
  }

  get glogetSalons(): GlogetSalonSetup[] {
    return this.setup?.locations.gloget ?? [];
  }

  get unmappedGlogetStaff(): GlogetStaffSetup[] {
    const mappedIds = new Set(
      this.squareStaff
        .filter((member) => !!member.mapping)
        .map((member) => member.mapping!.staff_id),
    );

    return this.glogetStaff.filter((staff) => !mappedIds.has(staff.id));
  }

  availableStaffFor(squareMember: SquareStaffSetup): GlogetStaffSetup[] {
    const mappedIds = new Set(
      this.squareStaff
        .filter((member) => !!member.mapping && member.id !== squareMember.id)
        .map((member) => member.mapping!.staff_id),
    );

    return this.glogetStaff.filter((staff) => !mappedIds.has(staff.id));
  }

  /* ========================================================
     Map existing
  ======================================================== */

  mapExisting(member: SquareStaffSetup): void {
    const staffId = this.selectedStaffBySquareMember[member.id];

    if (!staffId) {
      this.errorMessage = "Select a gloget staff member first.";

      return;
    }

    this.mappingTeamMemberId = member.id;

    this.successMessage = "";

    this.errorMessage = "";

    this.integrationsService
      .mapSquareStaff(member.id, staffId)
      .pipe(
        finalize(() => {
          this.mappingTeamMemberId = null;
        }),
      )
      .subscribe({
        next: () => {
          this.successMessage = `${member.display_name} has been mapped successfully.`;

          this.mappingChanged.emit();
        },

        error: (error) => {
          console.error("Unable to map Square staff:", error);

          this.errorMessage =
            error?.error?.detail ?? "Unable to map this Square staff member.";
        },
      });
  }

  /* ========================================================
     Square -> gloget
  ======================================================== */

  openCreateInGloget(member: SquareStaffSetup): void {
    this.selectedSquareMember = member;

    this.createGlogetError = "";

    this.createGlogetForm = {
      square_team_member_id: member.id,

      salon_id: "",

      first_name: member.given_name ?? "",

      last_name: member.family_name ?? "",

      email: member.email_address ?? "",

      phone_number: member.phone_number ?? "",

      job_title: "",
    };

    this.showCreateGlogetModal = true;
  }

  closeCreateGlogetModal(): void {
    if (this.createGlogetSubmitting) {
      return;
    }

    this.showCreateGlogetModal = false;

    this.selectedSquareMember = null;

    this.createGlogetError = "";

    this.createGlogetForm = this.emptyForm();
  }

  submitCreateInGloget(): void {
    const form = this.createGlogetForm;

    if (!form.salon_id) {
      this.createGlogetError =
        "Select the gloget salon this staff member belongs to.";

      return;
    }

    if (!form.first_name.trim()) {
      this.createGlogetError = "First name is required.";

      return;
    }

    if (!form.last_name.trim()) {
      this.createGlogetError = "Last name is required.";

      return;
    }

    if (!form.email.trim()) {
      this.createGlogetError = "Email is required.";

      return;
    }

    this.createGlogetSubmitting = true;

    this.createGlogetError = "";

    this.integrationsService
      .createGlogetStaffFromSquare({
        square_team_member_id: form.square_team_member_id,

        salon_id: form.salon_id,

        first_name: form.first_name.trim(),

        last_name: form.last_name.trim(),

        email: form.email.trim(),

        phone_number: form.phone_number.trim(),

        job_title: form.job_title.trim(),
      })
      .pipe(
        finalize(() => {
          this.createGlogetSubmitting = false;
        }),
      )
      .subscribe({
        next: () => {
          this.successMessage = `${this.selectedSquareMember?.display_name ?? "Staff member"} was created in gloget and mapped to Square.`;

          this.closeCreateGlogetModal();

          this.mappingChanged.emit();
        },

        error: (error) => {
          console.error("Unable to create gloget staff:", error);

          this.createGlogetError =
            error?.error?.detail ??
            "Unable to create this staff member in gloget.";
        },
      });
  }

  /* ========================================================
     gloget -> Square
  ======================================================== */

  createInSquare(staff: GlogetStaffSetup): void {
    const confirmed = window.confirm(
      `Create "${staff.name}" as a Square team member?`,
    );

    if (!confirmed) {
      return;
    }

    this.creatingSquareStaffId = staff.id;

    this.successMessage = "";

    this.errorMessage = "";

    this.integrationsService
      .createSquareStaffFromGloget(staff.id)
      .pipe(
        finalize(() => {
          this.creatingSquareStaffId = null;
        }),
      )
      .subscribe({
        next: (response) => {
          if (response?.requires_appointments_setup) {
            this.successMessage = `${staff.name} was created in Square and mapped to gloget. Square Appointments staff setup still needs to be completed before this person can receive Square bookings.`;
          } else {
            this.successMessage = `${staff.name} was created in Square and mapped to gloget.`;
          }

          this.mappingChanged.emit();
        },

        error: (error) => {
          console.error("Unable to create Square staff:", error);

          this.errorMessage =
            error?.error?.detail ??
            "Unable to create this team member in Square.";
        },
      });
  }

  private emptyForm(): CreateGlogetStaffForm {
    return {
      square_team_member_id: "",

      salon_id: "",

      first_name: "",

      last_name: "",

      email: "",

      phone_number: "",

      job_title: "",
    };
  }
}
